<template lang="pug">
  .panel-page-white
    .panel-white.body-1.mx-auto
      FormErrorAlert(
        :message="message"
        v-if="message"
        @dismiss="message = ''")
      PageHeader
        | {{ isEdit ? 'Edit' : 'Add'}} Person Watch
      p
        | The system will watch for records that match the criteria you enter below. When a matching record is found, you will be
        | notified by email.

      form(
        @submit.prevent="submit"
        autocomplete="off"
      )
        Expander.mt-3(
          heading="Biographical Information"
          :show.sync="showBio"
        )
          .form-row(v-for="watchName,index of watchNames",:key="index")
            .col
              .row
                .col
                  .form-group
                    label(:for="`lastName${index}`") Last Name
                    input.form-control(:id="`lastName${index}`",v-model="watchName.lastName")
                .col
                  .form-group
                    label(:for="`firstName${index}`") First Name
                    input.form-control(:id="`firstName${index}`",v-model="watchName.firstName")
                .col
                  .form-group
                    label(:for="`middleName${index}`") Middle Name
                    input.form-control(:id="`middleName${index}`",v-model="watchName.middleName")
                .col.remove-button
                  a(v-if="watchNames.length > 1",href="",@click.prevent="removePerson(index)") Remove
          .form-row.border-separator
            button.btn.btn-primary.frm-button(@click.prevent="addPerson") + Add Another Person
          .form-row.mt-3
            .col-3
              .form-group
                label(for="dob") Date of Birth
                Datepicker(v-model="dob" input-id="dob" input-class="form-control")
                span#dobHelp.form-text.text-muted.body-3 MM/DD/YYYY

        Expander.mt-3(
          heading="Unique Identification Numbers"
          :show.sync="showId"
        )
          .form-row(v-for="watchNumber,index of watchNumbers",:key="index")
            .col-6
              .form-group
                label(:for="`idNumber${index}`") ID Number
                input.form-control(:id="`idNumber${index}`", v-model="watchNumbers[index]")
            .col.remove-button
              a(v-if="watchNumbers.length > 1",href="",@click.prevent="removeNumber(index)") Remove
          .form-row
            button.btn.btn-primary.frm-button(@click.prevent="addNumber") + Add Another Unique Identifier

        Expander.mt-3(
          heading="Address"
          :show.sync="showAddress"
        )
          .form-row
            .col-8
              .form-group
                label(for="addressStreet") Street Address
                input#addressStreet.form-control(v-model="address1")
            .col
              .form-group
                label(for="addressApt") Apt/Suite
                input#addressApt.form-control(v-model="address2")
          .form-row
            .col
              .form-group
                label(for="addressCity") City
                input#addressCity.form-control(v-model="city")
            .col
              .form-group
                label(for="addressState") State
                CustomSelect(v-model="state",:options="states",labelName="name",valueName="abbreviation")
            .col
              .form-group
                label(for="addressZip") Zip
                input#addressZip.form-control(v-model="zip")
          .form-row
            CustomCheckbox(v-model="notifyAddress")
              span.body-1  Notify me if only the address matches (i.e. notify me if someone with the same address has activity)
        Expander.mt-3(
          heading="Locations"
          :show.sync="showLocations"
        )
          CustomRadio(modelValue="all",v-model="locationSelect")
            span.body-1.ml-2 All States
          br
          CustomRadio(modelValue="select",v-model="locationSelect")
            span.body-1.ml-2 Select States
          SuperSelect.mt-3(taggable
            v-model="locations"
            :options="filteredStates"
            v-if="locationSelect === 'select'"
            label-name="name"
            @filter="locationFilter = $event")
        Expander.mt-3(
          heading="Notification Options"
          :show.sync="showOptions"
        )
          p Send notifications to:
          div(v-if="$store.state.account.altEmail1")
            CustomCheckbox(v-model="alertAltEmail1")  {{ $store.state.account.altEmail1 }}
            HelpPopover(message="If you would like to update these email addresses permanently for all watch hits, please go to My Account and select Edit Profile.")
          div(v-if="$store.state.account.altEmail2")
            CustomCheckbox(v-model="alertAltEmail2")  {{ $store.state.account.altEmail2 }}
          div.mt-3(v-for="(email, index) of additionalEmails",:key="index")
            .form-group
              .form-row
                .col
                  label(:for="`email${index}`") Email Address
              .form-row
                .col
                  input.form-control(
                    :id="`email${index}`"
                    v-model="additionalEmails[index]"
                    :class="{'is-invalid': emailInvalid[index]}")
                .col.remove-button
                  a(href="",@click.prevent="removeEmail(index)") Remove
              .form-row
                .col
                  FormError(:message="stdmsg.emailError" v-show="emailInvalid[index]")
          .form-row.mt-3
            .col
              button.btn.btn-primary.frm-button(@click.prevent="addEmail") + Add Another Email Address
          .form-row.length-of-watch.mt-3
            .col
              span Length of watch
              template(v-if="isEdit")
                br
                CustomRadio(v-model="lengthOfWatch",model-value="current")  Keep current expiration date
              br
              CustomRadio(v-model="lengthOfWatch",model-value="3")  {{ useExtend }}3 months from today
              br
              CustomRadio(v-model="lengthOfWatch",model-value="6")  {{ useExtend }}6 months from today
              br
              CustomRadio(v-model="lengthOfWatch",model-value="12")  {{ useExtend }}12 months from today
              div.mt-3
                h5
                  | Expiration Date:
                  span.expiration-header(ref="expirationDate") &nbsp;{{ isEdit ? modifiedEditExpirationDate : expirationDate }}
          .form-row.mt-4
            .col
              .form-group
                label(for="watchNotes") Notes concerning this watch
                textarea#watchNotes.form-control(v-model="watchNotes")

          button.btn.btn-primary.mt-5.mr-2 Save
          router-link.btn.btn-light.mt-5(:to="{name: 'watch_list'}") Cancel
</template>

<script>
import CustomCheckbox from '../components/CustomCheckbox'
import CustomRadio from '../components/CustomRadio'
import CustomSelect from '../components/CustomSelect'
import Datepicker from '@/components/Datepicker'
import Expander from '../components/Expander'
import HelpPopover from '../components/HelpPopover'
import PageHeader from '../components/PageHeader'
import states from '../lib/states'
import { stateByAbbr } from '@/lib/states'
import stdmsg from '@/lib/stdmsg'
import SuperSelect from '../components/SuperSelect'
import axios from '@/lib/axios'
import { globalSuccess, globalError } from '@/lib/utils'
import { format, parse, addMonths } from 'date-fns/esm'

export default {
  name: 'AddWatch',
  data: function() {
    return {
      watchNames: [],
      watchNumbers: [''],
      stdmsg: stdmsg,
      message: '',
      dob: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      notifyAddress: false,
      states: states,
      superSelectStates: states,
      locationSelect: 'all',
      locations: [],
      locationFilter: '',
      alertAltEmail1: true,
      alertAltEmail2: true,
      additionalEmails: [],
      formSubmitted: false,
      lengthOfWatch: this.isEdit ? 'current' : '3',
      watchNotes: '',
      editExpiration: '', // The expiration date if this is an edit page instead of an add page
      showBio: true,
      showId: true,
      showAddress: true,
      showLocations: true,
      showOptions: true,
    }
  },
  methods: {
    newPerson() {
      return {
        lastName: '',
        firstName: '',
        middleName: '',
      }
    },
    removePerson(idx) {
      this.watchNames.splice(idx, 1)
    },
    addPerson() {
      this.watchNames.push(this.newPerson())
    },
    addNumber() {
      this.watchNumbers.push('')
    },
    removeNumber(idx) {
      this.watchNumbers.splice(idx, 1)
    },
    addEmail() {
      this.additionalEmails.push('')
    },
    removeEmail(idx) {
      this.additionalEmails.splice(idx, 1)
    },
    submit() {
      this.formSubmitted = true
      if (this.invalidForm) {
        this.message =
          'Please include at least one name, one ID or an address. Note: The "Notify me if only the address matches..." option may only be selected if an address is also provided.'
        scrollTo(0, 0)
        return
      }
      if (this.emailInvalid.some(e => e)) {
        this.message = stdmsg.formError
        scrollTo(0, 0)
        return
      }
      let submitUrl = this.isEdit ? '/api/watch' : '/api/watches'
      axios()
        .post(submitUrl, this.watchPost)
        .then(() => {
          globalSuccess(null, 'The watch was successfully saved.')
          this.$router.push({ name: 'watch_list' })
        })
        .catch(err => {
          globalError(err)
          scrollTo(0, 0)
        })
    },
    clearForm() {
      this.dob = ''
      this.address1 = ''
      this.address2 = ''
      this.city = ''
      this.state = ''
      this.zip = ''
      this.notifyAddress = false
      this.states = states
      this.superSelectStates = states
      this.locationSelect = 'all'
      this.locations = []
      this.locationFilter = ''
      this.alertAltEmail1 = true
      this.alertAltEmail2 = true
      this.additionalEmails = []
      this.formSubmitted = false
      this.lengthOfWatch = '3'
      this.watchNotes = ''
      this.editExpiration = '' // The expiration date if this is an edit page instead of an add page
      this.showBio = true
      this.showId = true
      this.showAddress = true
      this.showLocations = true
      this.showOptions = true
      this.watchNames = [{ firstName: '', lastName: '', middleName: '' }]
      this.watchNumbers = ['']
    },
  },
  computed: {
    selectedStates() {
      return this.locations.map(l => l.abbreviation)
    },
    filteredStates() {
      let ft = this.locationFilter.toLowerCase()
      if (ft === '') return this.superSelectStates
      return this.superSelectStates.filter(
        l =>
          l.name.toLowerCase().includes(ft) ||
          l.abbreviation.toLowerCase().includes(ft),
      )
    },
    isEdit() {
      return !!this.$route.params.watchSid
    },
    useExtend() {
      return this.isEdit ? 'Extend ' : ''
    },
    expirationDate() {
      let months =
        this.lengthOfWatch === 'current' ? 0 : parseInt(this.lengthOfWatch)
      let date = new Date()
      date.setMonth(date.getMonth() + months)
      return format(date, 'MM/dd/yyyy')
    },
    modifiedEditExpirationDate() {
      if (this.lengthOfWatch === 'current') return this.editExpiration
      let dt = parse(this.editExpiration, 'MM/dd/yyyy', new Date())
      dt = addMonths(dt, parseInt(this.lengthOfWatch))
      return format(dt, 'MM/dd/yyyy')
    },
    emailInvalid() {
      return this.additionalEmails.map(
        e => this.formSubmitted && !/[^@]+@.+\..+/.test(e),
      )
    },
    hasAddress() {
      return this.address1 && this.city && this.state
    },
    validIds() {
      return this.watchNumbers.filter(wn => wn && wn.length > 0)
    },
    validNames() {
      return this.watchNames.filter(
        wn => wn.firstName || wn.lastName || wn.middleName,
      )
    },
    watchPost() {
      return {
        nplexWatchSid: this.$route.params.watchSid,
        birthDt: this.dob && this.dob.length > 0 ? this.dob : null,
        watchMustMatchAddress: this.notifyAddress ? 'Y' : 'N',
        expirationTs: this.isEdit
          ? this.modifiedEditExpirationDate
          : this.expirationDate,
        alertAltEmail1: this.alertAltEmail1 ? 'Y' : 'N',
        alertAltEmail2: this.alertAltEmail2 ? 'Y' : 'N',
        additionalEmails: this.additionalEmails,
        notes: this.watchNotes,
        names: this.validNames,
        states: this.locationSelect === 'all' ? [] : this.selectedStates,
        ids: this.validIds,
        addresses: this.hasAddress
          ? [
              {
                address1: this.address1,
                address2: this.address2,
                city: this.city,
                stateCd: this.state,
                postalCode: this.zip,
              },
            ]
          : [],
      }
    },
    invalidForm() {
      return (
        (this.validNames.length === 0 &&
          this.validIds.length === 0 &&
          !this.hasAddress) ||
        (!this.hasAddress && this.notifyAddress)
      )
    },
  },
  watch: {
    expirationDate: function() {
      this.$refs.expirationDate.classList.add('expiration-changed')
      setTimeout(
        () => this.$refs.expirationDate.classList.remove('expiration-changed'),
        400,
      )
    },
  },
  beforeRouteEnter(to, _from, next) {
    if (to.params.watchSid) {
      axios()
        .get('/api/watch', { params: { sid: to.params.watchSid } })
        .then(resp => {
          next(vm => {
            let d = resp.data
            vm.watchNames =
              d.names.length > 0
                ? d.names
                : [{ firstName: '', lastName: '', middleName: '' }]
            vm.watchNumbers = d.ids.length > 0 ? d.ids : ['']
            vm.dob = d.birthDt
            if (d.addresses && d.addresses.length > 0) {
              vm.address1 = d.addresses[0].address1
              vm.address2 = d.addresses[0].address2
              vm.city = d.addresses[0].city
              vm.state = d.addresses[0].stateCd
              vm.zip = d.addresses[0].postalCode
            }
            vm.alertAltEmail1 = d.alertAltEmail1
            vm.alertAltEmail2 = d.alertAltEmail2
            for (let st of d.states) vm.locations.push(stateByAbbr(st))
            if (d.states.length > 0) vm.locationSelect = 'select'
            vm.notifyAddress = d.watchMustMatchAddress
            vm.watchNotes = d.notes
            vm.editExpiration = d.expirationTs
            vm.lengthOfWatch = 'current'
            vm.additionalEmails = d.additionalEmails
          })
        })
    } else {
      next(vm => {
        vm.watchNames = [{ firstName: '', lastName: '', middleName: '' }]
      })
    }
  },
  beforeRouteLeave(to, _from, next) {
    if (to.name === 'add_watch') {
      this.clearForm()
    }
    next()
  },
  mounted() {
    scrollTo(0, 0)
  },
  components: {
    Expander,
    CustomCheckbox,
    CustomRadio,
    SuperSelect,
    HelpPopover,
    CustomSelect,
    PageHeader,
    Datepicker,
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/custom';

.remove-button {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.frm-button {
  margin-left: 5px;
}
</style>
